<template>
    <div class="PublicProductsLiabilityCriterion">
        <JsonSchemaCustomForm
            v-model="controller.value"
            :controller="controller"
            touched
        >
            <CriterionSettingsRow
                class="py-2.5"
                data-test-id="PublicProductsLiabilityCriterion__acceptFieldRow"
                :schema="acceptField.form.schema"
            >
                <JsonSchemaCustomFormElement
                    :field="acceptField"
                    :custom-component-input="customComponentInput"
                />
            </CriterionSettingsRow>
            <CriterionSettingsRow
                data-test-id="PublicProductsLiabilityCriterion__aggregateLimitFieldRow"
                class="pb-2.5"
                :class="{'PublicProductsLiabilityCriterion__settingRow--disabled': !showAggregateLimit}"
                :schema="aggregateLimitField.form.schema"
            >
                <JsonSchemaCustomFormElement
                    :field="aggregateLimitField"
                    :custom-component-input="customComponentInput"
                    show-error
                />
            </CriterionSettingsRow>
            <CriterionSettingsRow
                data-test-id="PublicProductsLiabilityCriterion__occurrenceLimitFieldRow"
                class="pb-2.5"
                :class="{'PublicProductsLiabilityCriterion__settingRow--disabled': !showOccurrenceLimit}"
                :schema="occurrenceLimitField.form.schema"
            >
                <JsonSchemaCustomFormElement
                    :field="occurrenceLimitField"
                    :custom-component-input="customComponentInput"
                    show-error
                />
            </CriterionSettingsRow>
            <CriterionSettingsRow
                data-test-id="PublicProductsLiabilityCriterion__umbrellaFieldRow"
                class="pb-2.5"
                :schema="umbrellaField.form.schema"
            >
                <JsonSchemaCustomFormElement
                    :field="umbrellaField"
                    :custom-component-input="customComponentInput"
                    show-error
                />
            </CriterionSettingsRow>
        </JsonSchemaCustomForm>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import {
        JsonSchemaCustomForm,
        JsonSchemaCustomFormElement,
    } from '@evidentid/dashboard-commons/components/JsonSchemaForm';
    import createForm from '@evidentid/json-schema/createForm';
    import {
        FormController,
    } from '@evidentid/json-schema/FormController';
    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { CriterionTemplate } from '@evidentid/tprm-portal-lib/models/decisioning/Criterion.model';

    import CriterionSettingsRow
        from '@/modules/decisioning-criteria/components/CriterionSettingsRow/CriterionSettingsRow.vue';
    import {
        EditableRequirementCriterion,
    } from '@/modules/decisioning-criteria/models/EditableRequirements.model';
    import { buildCriterionSchema } from '@/modules/decisioning-criteria/schemas/buildCriterionSchema';
    import { requirementTypeFieldReferenceDelimiter } from '@/modules/decisioning-criteria/types';
    import {
        getCriterionJsonSchemaComponent,
    } from '@/modules/decisioning-criteria/utils/getCriterionJsonSchemaComponent';
    import { standardizeCriterionSchema } from '@/modules/decisioning-criteria/utils/standardizeCriterionSchema';

    type LoadCriterionMessage = (
        controller: FormController,
        updatedCriterion: EditableRequirementCriterion,
    ) => void;

    enum AcceptForValue {
        perOccurrence = 'PER_OCCURRENCE_LIMIT_ONLY',
        aggregateLimitOnly = 'AGGREGATE_LIMIT_ONLY',
    }

    enum Reference {
        productsLiability = 'productsLiabilityConfig',
        publicLiability = 'publicLiabilityConfig',
    }

    export default defineComponent({
        name: 'PublicProductsLiabilityCriterion',
        components: {
            JsonSchemaCustomForm,
            JsonSchemaCustomFormElement,
            CriterionSettingsRow,
        },
        props: {
            criterion: {
                type: Object as PropType<EditableRequirementCriterion>,
                required: true,
            },
            template: {
                type: Object as PropType<CriterionTemplate>,
                required: true,
            },
            loadCriterionMessage: {
                type: Function as PropType<LoadCriterionMessage>,
                default: () => undefined,
            },
            customProperties: {
                type: Array as PropType<CustomProperty[]>,
                default: (): CustomProperty[] => [] as CustomProperty[],
            },
        },
        emits: [ 'controller-value-update', 'input', 'reference-accessed' ],
        data() {
            const orderedTemplateReferences = Object.keys(this.template.evaluator.references) || [];
            const value = { ...this.criterion.references };
            const schema = standardizeCriterionSchema(
                buildCriterionSchema(this.criterion.references, orderedTemplateReferences, this.template),
                this.customProperties,
            );
            const form = createForm(schema);
            const controller = new FormController(form, form.getValue(value, true));
            return {
                controller,
                customComponentInput: getCriterionJsonSchemaComponent(),
            };
        },
        computed: {
            referenceName(): Reference {
                return this.template.field === 'coverage.productsLiability'
                    ? Reference.productsLiability
                    : Reference.publicLiability;
            },
            showAggregateLimit(): boolean {
                return this.controller.value[this.referenceName].acceptedFor !== AcceptForValue.perOccurrence;
            },
            showOccurrenceLimit(): any {
                return this.controller.value[this.referenceName].acceptedFor !== AcceptForValue.aggregateLimitOnly;
            },
            acceptField(): any {
                return this.controller.structure.properties?.[this.referenceName]?.properties?.acceptedFor;
            },
            aggregateLimitField(): any {
                return this.controller.structure.properties?.[this.referenceName]?.properties?.aggregateLimitValue;
            },
            occurrenceLimitField(): any {
                return this.controller.structure.properties?.[this.referenceName]?.properties?.perOccurrenceLimitValue;
            },
            umbrellaField(): any {
                return this.controller.structure.properties?.allowUmbrellaExcess;
            },
            computedValue(): Record<string, any> {
                return { ...this.controller.form.getValue(this.controller.value) };
            },
        },
        watch: {
            computedValue: {
                handler(newValue: any) {
                    const reference = [
                        this.criterion.coverageType,
                        this.criterion.field,
                        this.referenceName,
                    ].join(requirementTypeFieldReferenceDelimiter);
                    const updatedCriterion: EditableRequirementCriterion = {
                        ...this.criterion,
                        references: {
                            ...this.criterion.references,
                            [this.referenceName]: { ...newValue[this.referenceName] },
                            allowUmbrellaExcess: newValue.allowUmbrellaExcess,
                        },
                    };
                    this.$emit('input', updatedCriterion);
                    this.$emit('reference-accessed', { reference, isValid: this.controller.valid });
                    this.$emit('controller-value-update', this.controller.value);
                    this.loadCriterionMessage?.(this.controller as FormController, updatedCriterion);
                },
            },
        },
        created() {
            this.loadCriterionMessage?.(this.controller as FormController, this.criterion);
            this.$emit('controller-value-update', this.controller.value);
        },
    });
</script>
